// @flow
import React, { useEffect, useState } from 'react';
// Components
import VisibilitySensor from 'react-visibility-sensor';
import { Link } from 'react-router-dom';
import { ProgressiveImageLoader } from '../../../common';
// Analytics
import * as Analytics from '../../../common/analytics';
// Helpers
import {
  scaleConverter,
  priceConverter,
  quantityConverter,
} from '../../../common';
// Styles
import './ResumeItem.css';
import { fatLevels } from '../../constants/fatLevels';

type Props = {
  productImage: ?ProductImage,
  productName: string,
  productManufacturer: string,
  productListPrice: number,
  productPrice: number,
  productUnit: string,
  productSlug: string,
  productId: number,
  defaultQuantity: number,
  incrementQuantity: number,
  inStock: boolean,
  discontinued: boolean,
  quantity: number,
  fatLevel: string,
  packagingName: string,
  portioningSize: number,
  isFreebie: boolean,
};

const ResumeItem = ({
  productSlug,
  productImage,
  productName,
  productPrice,
  productListPrice,
  productManufacturer,
  productUnit,
  productId,
  parentProductId,
  productTotalOnHand,
  defaultQuantity,
  inStock,
  discontinued,
  incrementQuantity,
  analyticsViewSource,
  quantity,
  fatLevel,
  packagingName,
  portioningSize,
  giftCard,
  isFreebie
}: Props) => {
  const [isProductVisible, setIsProductVisible] = useState(false);
  const price = scaleConverter(productPrice, productUnit);
  const listPrice = scaleConverter(productListPrice);
  const productOnPromotion = listPrice > price;
  const productDiscount = productOnPromotion
    ? ((listPrice - price) / listPrice) * 100
    : null;

  useEffect(() => {
    if (isProductVisible) {
      Analytics.logProductImpression(
        {
          variantId: productId,
          productId: parentProductId,
          name: productName,
          price: productPrice,
          manufacturer: productManufacturer,
          unitForQuantity: productUnit,
          totalOnHand: productTotalOnHand,
          listPrice: productListPrice,
          inStock: inStock,
          discontinued: discontinued,
          images: productImage ? [productImage] : [],
          incrementQuantity: incrementQuantity,
          defaultQuantity: defaultQuantity,
        },
        window.location.pathname,
        analyticsViewSource,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductVisible]);

  const portionNumber = Math.floor((quantity / portioningSize))
  let remainder = quantity - (portioningSize * portionNumber)

  const unitForQuantityPortioning = portioningSize >= 1000 ? 'Kg' : 'gramas'
  const unitForQuantityRemainder = remainder >= 1000 ? 'Kg' : 'gramas'

  const portioningSizeValue = portioningSize >= 1000 ? portioningSize / 1000 : portioningSize
  remainder = remainder >= 1000 ? remainder / 1000 : remainder

  const isGiftCard = !!giftCard?.id
  const formattedDate = giftCard?.sendAt 
    ? new Date(giftCard.sendAt).toLocaleDateString('pt-BR') 
    : '';
  return (
    <VisibilitySensor
      onChange={isVisible => {
        setIsProductVisible(isVisible);
      }}
    >
      <div className="resume-item-wrapper">
        <section className="resume-item-image-section">
          <div className="resume-item-link">
            {productOnPromotion && productDiscount >= 5 && (
              <div className="resume-item-image-discount">
                <p>{Math.round(productDiscount)}%</p>
                <span>OFF</span>
              </div>
            )}
            <ProgressiveImageLoader
              selectedImage={productImage}
              imageToShow="product"
            />
          </div>
          <Link className="resume-item-link" to={`/produtos/${productSlug}/`}>
            <p className="resume-item-variant">{productManufacturer}</p>
            <h3 title={productName} className="resume-item-title">
              {productName}
            </h3>
          </Link>
        </section>
        {isGiftCard ? <section className="resume-item-info-section">
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Preço
            </div>
            <div className="resume-gift-card-info-row-text">{priceConverter(price)}</div>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Nome
            </div>
            <span className="resume-gift-card-info-row-quantity">
              {giftCard.receiverName}
            </span>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Email
            </div>
            <span className="resume-gift-card-info-row-quantity">
              {giftCard.email}
            </span>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Envio
            </div>
            <span className="resume-gift-card-info-row-quantity">
              {formattedDate}
            </span>
          </div>
          <div className="resume-item-info-row"></div>
          <div class="resume-item-portioning-info">
            {!!portioningSize && portioningSize !== quantity   && 
              <>{portionNumber} embalagem de {portioningSizeValue} {unitForQuantityPortioning}
              {remainder > 0 && (
                <> + 1 embalagem de {remainder} {unitForQuantityRemainder} </>
              )}</>
            }
            </div>
          <div className="resume-item-info-row">
            <h3 title={productName} className="resume-item-title">
              Preço Estimado
            </h3>
            {!isFreebie ? <div className="resume-item-text-price"> 
              {parseFloat(
                price,
              ).toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}</div> : <div className="resume-item-freebie-label">BRINDE</div>}
          </div>
          <div className="resume-item-info-row"></div>
        </section> : <section className="resume-item-info-section">
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Preço/
              {productUnit === 'g' || productUnit === 'kg' ? 'Kg' : 'un'}
            </div>
            <div className="resume-item-info-row-text">{priceConverter(price)}</div>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Quantidade
            </div>
            <span className="resume-item-info-row-quantity">
              {quantityConverter(quantity, productUnit)}
            </span>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Nível de Gordura
            </div>
            <span className="resume-item-info-row-quantity">
              {fatLevels[fatLevel] || 'Padrão'}
            </span>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Embalagem a Vácuo
            </div>
            <span className="resume-item-info-row-quantity">
              {packagingName === 'vacuum' ? 'Sim' : 'Não'}
            </span>
          </div>
          <div className="resume-item-info-row">
            <div className="resume-item-info-row-title">
              Porcionamento
            </div>
            <span className="resume-item-info-row-quantity">
              {portioningSize && portioningSize !== quantity ? 'Sim' : 'Não'}
            </span>
          </div>
          <div class="resume-item-portioning-info">
            {!!portioningSize && portioningSize !== quantity && 
              <>{portionNumber} embalagem de {portioningSizeValue} {unitForQuantityPortioning}
              {remainder > 0 && (
                <> + 1 embalagem de {remainder} {unitForQuantityRemainder} </>
              )}</>
            }
            </div>
          <div className="resume-item-info-row">
            <h3 title={productName} className="resume-item-title">
              Preço Estimado
            </h3>
            {!isFreebie ? <div className="resume-item-text-price"> 
              {parseFloat(
                price,
              ).toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}</div> : <div className="resume-item-freebie-label">BRINDE</div>}
          </div>
          <div className="resume-item-info-row"></div>
        </section>}
      </div>
    </VisibilitySensor>
  );
};

export default ResumeItem;
